// PRD `weightsample-individual-length-v1.md` §3 item 8 / §5. Pure,
// storage-free aggregate math over individual WeightSampleLog readings —
// mirrors feedConversionRatio.ts's shape (PR #166 precedent): one formula
// module, no Dexie/network access, consumed only by the Reports dataset
// layer (never by a Crew Mode capture screen — Charter O-1, O-3).
//
// Deliberately separate from samplingEventMath.ts, which is scoped (by its
// own WSL-08 history) to mean-only math for two unrelated, non-Crew-Mode
// callers (SamplingEventsPopover, productionLegacyDuplicateMergePlan).

export type IndividualSampleReading = {
  weightGrams: number
  lengthMm?: number
}

/**
 * Coefficient of variation (%) of weight across individual readings —
 * sample standard deviation (n-1), the standard uniformity-assessment
 * convention. Requires at least 2 readings; returns null otherwise so the
 * caller renders a blank cell rather than a misleading zero.
 */
export const computeWeightCvPercent = (weights: number[]): number | null => {
  const values = weights.filter((value) => Number.isFinite(value) && value > 0)
  if (values.length < 2) return null

  const mean = values.reduce((sum, value) => sum + value, 0) / values.length
  if (mean <= 0) return null

  const variance = values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / (values.length - 1)
  const stdev = Math.sqrt(variance)

  return (stdev / mean) * 100
}

/**
 * Fulton's condition factor for one individual: K = 100000 x W(g) / L(mm)^3.
 * This is the standard K = 100 x W(g) / L(cm)^3 formula, substituting
 * L(cm) = L(mm) / 10 — this codebase stores length in millimeters, so the
 * 100000 constant (not 100) is required. Get this wrong and every K value
 * downstream is silently off by a power of ten.
 */
export const computeFultonK = (weightGrams: number, lengthMm: number): number | null => {
  if (!Number.isFinite(weightGrams) || weightGrams <= 0) return null
  if (!Number.isFinite(lengthMm) || lengthMm <= 0) return null
  return (100000 * weightGrams) / (lengthMm ** 3)
}

/**
 * Mean Fulton's K across every reading that carries both weight and
 * length. Readings with no lengthMm (weight-only) are excluded, not
 * treated as zero.
 */
export const computeMeanFultonK = (readings: IndividualSampleReading[]): number | null => {
  const values: number[] = []
  for (const reading of readings) {
    if (typeof reading.lengthMm !== 'number') continue
    const k = computeFultonK(reading.weightGrams, reading.lengthMm)
    if (k !== null) values.push(k)
  }

  if (values.length === 0) return null
  return values.reduce((sum, value) => sum + value, 0) / values.length
}
